import dayjs from "dayjs";

import { SalesMeetingSaleStatus, SalesMeetingStatus } from "@fitness-app/data-models/entities/SalesMeeting";

import { MANUAL_MEETING_URI_PREFIX, ORDER_OPTION_PREFIX, PROGRAM_HOLLYWOOD_PRODUCT_ID } from "./salesCrmConstants";
import { type BundleOptions } from "./hooks/useBundleOptionsQuery";
import { type SalesMeetingBundle, type SalesMeetingRow } from "./hooks/useSalesMeetingsQuery";

export const normalizeCurrency = (currency: string | null | undefined) => (currency || "PLN").toUpperCase();

export const formatMoney = (value: number | null | undefined, currency = "PLN") =>
  typeof value === "number"
    ? new Intl.NumberFormat("pl-PL", {
        style: "currency",
        currency: normalizeCurrency(currency),
        minimumFractionDigits: 2,
      }).format(value / 100)
    : "-";

// Strip characters that are structural in PostgREST `or(...)` filter strings
// and escape LIKE wildcards so e.g. underscores in e-mails match literally.
export const escapePostgrestPattern = (value: string) =>
  value
    .replace(/[,()]/g, " ")
    .replace(/[%_]/g, (wildcard) => `\\${wildcard}`)
    .trim();

// Settlements bucket a bundle as installments purely on `cycles is not null`,
// so only an integer >= 2 counts — anything else (1, fractional, cleared) is a
// one-time payment and must persist as null.
export const normalizeCycles = (value: number | null | undefined): number | null =>
  typeof value === "number" && Number.isInteger(value) && value > 1 ? value : null;

export const isManualMeeting = (meeting: SalesMeetingRow): boolean =>
  meeting.calendlyInviteeUri.startsWith(MANUAL_MEETING_URI_PREFIX);

export const getMeetingBundle = (meeting: SalesMeetingRow): SalesMeetingBundle | null =>
  meeting.customerBundle ?? meeting.matchedCustomerBundle ?? null;

// The seller closed a sale on the call, whatever the product.
const isSoldMeeting = (meeting: SalesMeetingRow): boolean =>
  meeting.status === SalesMeetingStatus.Attended && meeting.saleStatus === SalesMeetingSaleStatus.Yes;

// A won call is a Program Hollywood sale. A sale without a linked bundle (e.g. a
// free order from a seller's checkout code creates none) has no product to
// check, so it keeps counting as won.
export const isWonMeeting = (meeting: SalesMeetingRow): boolean => {
  if (!isSoldMeeting(meeting)) return false;

  const productId = getMeetingBundle(meeting)?.productId;

  return !productId || productId === PROGRAM_HOLLYWOOD_PRODUCT_ID;
};

// Amounts cover every product sold on the call, not only Program Hollywood.
export const getConfirmedSaleBundle = (meeting: SalesMeetingRow): SalesMeetingBundle | null =>
  isSoldMeeting(meeting) ? (meeting.customerBundle ?? null) : null;

// Purchases attributable to a meeting: paid up to 7 days before the call
// (booked-then-paid-early) or up to 60 days after it (the sales window from
// TRE-1525). Options come sorted newest-first, so the first hit wins.
export const pickLinkCandidates = (options: BundleOptions, meetingStartTime: string) => {
  const withinWindow = (createdAt: string | null | undefined) => {
    if (!createdAt) return false;
    const diffDays = dayjs(createdAt).diff(dayjs(meetingStartTime), "day", true);
    return diffDays >= -7 && diffDays <= 60;
  };

  return {
    bundleIds: options.bundles.filter((bundle) => withinWindow(bundle.createdAt)).map((bundle) => bundle.id),
    orderValues: options.orders
      .filter((order) => withinWindow(order.createdAt))
      .map((order) => `${ORDER_OPTION_PREFIX}${order.id}`),
  };
};

export const getBundleClientPath = (bundle: SalesMeetingBundle, fallbackEmail: string): string | null => {
  if (!bundle.productId) return null;

  return `/products/${bundle.productId}/clients/${encodeURIComponent(bundle.clientEmail || fallbackEmail)}`;
};
